// CompanyGeneralInfo.jsx
import React from 'react';
import { Building2, Factory, Truck, Store, Settings, Calendar, Users } from 'lucide-react';
import './CompanyGeneralInfo.css';

const CompanyGeneralInfo = ({ data, onChange, validationErrors }) => {
  const handleOtherFieldChange = (field, value) => {
    onChange(field, value);
  };

  const getErrorClass = (fieldName) => {
    return validationErrors[fieldName] ? 'error' : '';
  };

    // Helper function to render error message
  const renderError = (fieldName) => {
    if (validationErrors[fieldName]) {
      return (
        <div className="error-message" role="alert">
          {validationErrors[fieldName]}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="step-content">
      <div className="step-header-section">
        {/* <Building2 className="section-icon" size={32} /> */}
        <h2>أولاً: المعلومات العامة عن الشركة</h2>
      </div>

      {/* Company Name */}
      <div className="">
        <div className="question-header">
          <span className="question-number">1</span>
          <label className="question-title" htmlFor="companyName">
            اسم الشركة <span className="optional-note">(اختياري)</span>:
          </label>
        </div>
        <div className="text-input-container">
          <input
            id="companyName"
            type="text"
            value={data.companyName || ''}
            onChange={(e) => onChange('companyName', e.target.value)}
            placeholder="اسم الشركة..."
            className="text-input"
          />
        </div>
      </div>

      {/* Activity Type */}
      <div className="">
        <div className="question-header">
          <span className="question-number">2</span>
          <label className="question-title">حدد نوع النشاط:<span className="required-indicator">*</span></label>
        </div>

        <div className="data-category">
          {/* <div className="category-header">
            <Factory className="category-icon" size={24} />
            <h4>نوع النشاط:</h4>
          </div> */}

          <div className={`radio-options-grid ${getErrorClass('activityType')}`}>
            {[
              { value: 'صناعي', icon: '🏭' },
              { value: 'زراعي', icon: '🌾' },
              { value: 'تجاري', icon: '🏪' },
              { value: 'خدمي', icon: '🏢' },
              { value: 'أخرى', icon: '⚙️' }
            ].map(option => (
              <label key={option.value} className="radio-option">
                <input
                  type="radio"
                  name="activityType"
                  value={option.value}
                  checked={data.activityType === option.value}
                  onChange={(e) => onChange('activityType', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <span className="radio-text">{option.value}</span>
              </label>
            ))}
          </div>

          {data.activityType === 'أخرى' && (
            <div className="other-input-group">
              <label className="other-label" htmlFor="other-activity">أخرى (يرجى ذكرها):</label>
              <input
                id="other-activity"
                type="text"
                value={data.otherActivityType || ''}
                onChange={(e) => handleOtherFieldChange('otherActivityType', e.target.value)}
                placeholder="نوع النشاط الآخر..."
                className="other-input"
              />
            </div>
          )}
          {renderError('activityType')}
        </div>
      </div>

      {/* Activity Field */}
      <div className="">
        <div className="question-header">
          <span className="question-number">3</span>
          <label className="question-title" htmlFor="activityField">
            حدد مجال النشاط:<span className="required-indicator">*</span>
          </label>
        </div>
        <div className="text-input-container">
          <input
            id="activityField"
            type="text"
            value={data.activityField || ''}
            onChange={(e) => onChange('activityField', e.target.value)}
            placeholder="(مثل: تقنية المعلومات، التصنيع، الخدمات المصرفية...)"
            className="text-input"
            required
          />
        </div>
      </div>

      {/* Company Size */}
      <div className="">
        <div className="question-header">
          <span className="question-number">4</span>
          <label className="question-title">حدد حجم الشركة:<span className="required-indicator">*</span></label>
        </div>

        <div className="data-category">
          {/* <div className="category-header">
            <Users className="category-icon" size={24} />
            <h4>حجم الشركة:</h4>
          </div> */}

          <div className={`radio-options-grid ${getErrorClass('companySize')}`}>
            {[
              { value: 'صغيرة (أقل من 50 موظف)', icon: '👥', color: '#10b981' },
              { value: 'متوسطة (51-250 موظف)', icon: '👫', color: '#f59e0b' },
              { value: 'كبيرة (251-1000 موظف)', icon: '👪', color: '#3b82f6' },
              { value: 'كبيرة جداً (أكثر من 1001 موظف)', icon: '🏢', color: '#8b5cf6' }
            ].map(option => (
              <label key={option.value} className="radio-option size-option">
                <input
                  type="radio"
                  name="companySize"
                  value={option.value}
                  checked={data.companySize === option.value}
                  onChange={(e) => onChange('companySize', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <span className="radio-text">{option.value}</span>
              </label>
            ))}
          </div>
          {renderError('companySize')}
        </div>
      </div>

      {/* Company Age */}
      <div className="">
        <div className="question-header">
          <span className="question-number">5</span>
          <label className="question-title">حدد عدد سنوات الشركة:<span className="required-indicator">*</span></label>
        </div>

        <div className="data-category">
          {/* <div className="category-header">
            <Calendar className="category-icon" size={24} />
            <h4>عمر الشركة:</h4>
          </div> */}

          <div className={`radio-options-grid ${getErrorClass('companyAge')}`}>
            {[
              { value: 'أقل من 5 سنوات', icon: '🌱', desc: 'شركة ناشئة' },
              { value: '5 إلى 10 سنوات', icon: '🌿', desc: 'شركة نامية' },
              { value: '11 إلى 15 سنوات', icon: '🌳', desc: 'شركة راسخة' },
              { value: '16 إلى 20 سنوات', icon: '🏛️', desc: 'شركة عريقة' },
              { value: 'أكثر من 20 سنة', icon: '🏆', desc: 'شركة رائدة' }
            ].map(option => (
              <label key={option.value} className="radio-option age-option">
                <input
                  type="radio"
                  name="companyAge"
                  value={option.value}
                  checked={data.companyAge === option.value}
                  onChange={(e) => onChange('companyAge', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <div className="option-content">
                  <div className="option-text-group">
                    <span className="radio-text">{option.value}</span>
                    <span className="option-description">{option.desc}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          {renderError('companyAge')}
        </div>
      </div>

      {/* Respondent Position */}
      <div className="">
        <div className="question-header">
          <span className="question-number">6</span>
          <label className="question-title">حدد المنصب الوظيفي للمستجيب:<span className="required-indicator">*</span></label>
        </div>

        <div className="data-category">
          {/* <div className="category-header">
            <Settings className="category-icon" size={24} />
            <h4>المنصب الوظيفي:</h4>
          </div> */}

          <div className={`radio-options-grid ${getErrorClass('respondentPosition')}`}>
            {[
              { value: 'الرئيس التنفيذي / المدير العام', icon: '👔' },
              { value: 'نائب الرئيس التنفيذي / كبير الاداريين', icon: '💼' },
              { value: 'مدير الشؤون الإدارية و المالية', icon: '📊' },
              { value: 'مدير الموارد البشرية', icon: '👥' },
              { value: 'مدير التوظيف والاستقطاب', icon: '🎯' },
              { value: 'أخر', icon: '⚙️' }
            ].map(option => (
              <label key={option.value} className="radio-option age-option">
                <input
                  type="radio"
                  name="respondentPosition"
                  value={option.value}
                  checked={data.respondentPosition === option.value}
                  onChange={(e) => onChange('respondentPosition', e.target.value)}
                />
                <span className="radio-custom" aria-hidden="true"></span>
                <span className="radio-text">{option.value}</span>
              </label>
            ))}
          </div>

          {data.respondentPosition === 'أخر' && (
            <div className="other-input-group">
              <label className="other-label" htmlFor="other-position">أخر (يرجى ذكره):</label>
              <input
                id="other-position"
                type="text"
                value={data.otherPosition || ''}
                onChange={(e) => handleOtherFieldChange('otherPosition', e.target.value)}
                placeholder="المنصب الوظيفي الآخر..."
                className="other-input"
              />
            </div>
          )}
        </div>
        {renderError('respondentPosition')}
      </div>
    </div>
  );
};

export default CompanyGeneralInfo;